import type {
    Media,
    Resource,
    ResourceLight,
    ResourceThumbnail,
} from '@wwf/sdk';

export type MediaResource = Resource | ResourceLight | Media;

export type MediaThumbnailInput = string | ResourceThumbnail | null | undefined;

export function resourceDisplayName(media: MediaResource): string {
    return media.name ?? '';
}

/** Normalize the API `ResourceType` or legacy lowercase values for UI branches. */
export function resourceMediaKind(
    type: string | null | undefined
): 'image' | 'video' | 'audio' | 'document' | 'other' {
    const t = (type ?? '').toLowerCase();
    if (t === 'image') return 'image';
    if (t === 'video') return 'video';
    if (t === 'audio') return 'audio';
    if (t === 'document') return 'document';
    return 'other';
}

function parseResourceThumbnail(
    thumbnail: MediaThumbnailInput
): ResourceThumbnail | undefined {
    if (thumbnail == null) return undefined;

    if (typeof thumbnail === 'string') {
        const trimmed = thumbnail.trim();
        if (!trimmed) return undefined;
        if (trimmed.startsWith('data:') || /^https?:\/\//i.test(trimmed)) {
            return { data: trimmed, width: 0, height: 0 };
        }
        try {
            const parsed = JSON.parse(trimmed) as ResourceThumbnail;
            if (typeof parsed?.data === 'string' && parsed.data) {
                return parsed;
            }
        } catch {
            // not JSON — fall through
        }
        return { data: trimmed, width: 0, height: 0 };
    }

    return thumbnail.data ? thumbnail : undefined;
}

/** Resolve a grid/list thumbnail src from API jsonb, object, or legacy string. */
export function resourceThumbnailSrc(
    thumbnail: MediaThumbnailInput
): string | undefined {
    return parseResourceThumbnail(thumbnail)?.data;
}

/** Thumbnail pixel size when stored as jsonb `{ data, width, height }`. */
export function resourceThumbnailSize(
    thumbnail: MediaThumbnailInput
): { width: number; height: number } | undefined {
    const parsed = parseResourceThumbnail(thumbnail);
    if (!parsed?.width || !parsed?.height) {
        return undefined;
    }
    return { width: parsed.width, height: parsed.height };
}
